import {
    Grid2,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { deleteTicketOption } from '@openapi/ticket-options/ticket-options';
import useNotify from '../../../hooks/useNotify';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../../hooks/useCapabilities';
import useDragReorder from '../../../hooks/useDragReorder';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ id, children, reorderMode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: !reorderMode,
    });
    return (
        <TableRow
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
                cursor: reorderMode ? (isDragging ? 'grabbing' : 'grab') : undefined,
                position: 'relative',
                zIndex: isDragging ? 1 : 0,
            }}
            {...(reorderMode ? { ...attributes, ...listeners } : {})}
        >
            {children}
        </TableRow>
    );
};

const TicketOptionsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    sort,
    sortDir,
    setSorting,
    showId,
    reorderMode,
    onOrderChange,
}) => {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageShows = can(CAPABILITIES.SHOWS_MANAGE);
    const [deletingId, setDeletingId] = useState(null);
    const { localRows, handleDragEnd } = useDragReorder(rows, onOrderChange);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDeleteTicketOption = useCallback(
        async (id) => {
            setDeletingId(id);
            try {
                await deleteTicketOption(id);
                notifySuccess(t('ticketoptions.notifications.deleted'));
                reloadData();
            } catch (_error) {
                notifyError(t('ticketoptions.notifications.deleteError'));
            } finally {
                setDeletingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    const displayRows = reorderMode ? localRows : rows;

    return (
        <Grid2 size={{ xs: 12 }}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 640 }}>
                        <TableHead>
                    <TableRow>
                        {reorderMode && <TableCell sx={{ width: 40 }} />}
                        <SortableTableCell
                            arrow={sort === 'name' ? sortDir : ''}
                            onClick={() => setSorting('name')}
                        >
                            {t('ticketoptions.columns.name')}
                        </SortableTableCell>
                        <SortableTableCell
                            arrow={sort === 'description' ? sortDir : ''}
                            onClick={() => setSorting('description')}
                        >
                            {t('ticketoptions.columns.description')}
                        </SortableTableCell>
                        <SortableTableCell
                            arrow={sort === 'ageBand' ? sortDir : ''}
                            onClick={() => setSorting('ageBand')}
                        >
                            {t('ticketoptions.columns.ageBand')}
                        </SortableTableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                    <SortableContext
                        items={displayRows?.map((r) => r.id) || []}
                        strategy={verticalListSortingStrategy}
                    >
                        <TableBody>
                            {loading ? (
                                <SkeletonTable rows={limit} cells={reorderMode ? 5 : 4} />
                            ) : (
                                displayRows?.map((row) => (
                                    <SortableRow key={row.id} id={row.id} reorderMode={reorderMode}>
                                        {reorderMode && (
                                            <TableCell sx={{ color: 'text.secondary', width: 40 }}>
                                                <DragIndicatorIcon fontSize="small" />
                                            </TableCell>
                                        )}
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell>
                                            {row.ageBand
                                                ? row.ageBand.charAt(0).toUpperCase() +
                                                  row.ageBand.slice(1)
                                                : t('ticketoptions.ageBand.notDefined')}
                                        </TableCell>
                                        {canManageShows && (
                                            <TableCell
                                                align="right"
                                                onPointerDown={(e) => e.stopPropagation()}
                                            >
                                                <Link
                                                    to={`/shows/${showId}/ticketoptions/edit/${row.id}`}
                                                >
                                                    <Tooltip title={t('common.edit')}>
                                                        <IconButton>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Link>
                                                <IconButton
                                                    onClick={() => {
                                                        showModal(MODAL_TYPE.CONFIRMATION, {
                                                            title: t('confirmationModal.title'),
                                                            content: t('confirmationModal.content'),
                                                            confirm: () =>
                                                                handleDeleteTicketOption(row.id),
                                                        });
                                                    }}
                                                    disabled={deletingId === row.id}
                                                >
                                                    <Tooltip title={t('common.delete')}>
                                                        <DeleteIcon />
                                                    </Tooltip>
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </SortableRow>
                                ))
                            )}
                        </TableBody>
                    </SortableContext>
                    </Table>
                </TableContainer>
            </DndContext>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default TicketOptionsTable;
